// Desktop bento mosaic gallery for the space page.
//
// Adaptive to how many real photos the Space has, so the left column always feels
// intentional and complete — never a grid with empty holes or a placeholder:
//   • 1 photo  → a single large protagonist cover that fills the whole column.
//   • 5+ photos → one feature tile (2x2) + a 2x2 grid of smaller tiles, with a
//     "+N" overlay on the last visible tile when more photos exist.
// Mobile/tablet keeps the existing swiper ImagesCarousel. Clicking any tile opens
// the shared full-screen ImgsViewer (parity with the carousel lightbox).

import Image from 'next/image';
import React, { useCallback, useEffect, useState } from 'react';
import ImgsViewer from './Common/ImagesViewer/ImagesViewer';
import { filterValidImages } from '@/utils/spaceImages';

type GalleryImage = { url: string; alt: string; unoptimized?: boolean };

// Heights tuned so the gallery lines up with the booking panel on the right.
// The multi-photo mosaic stays tall; the single-photo hero uses a wide ~2:1 ratio
// (Luma/Airbnb-style "image as context") so it never dominates the viewport and the
// name + info to its right carry equal visual weight.
const HEIGHT = 'h-[320px] sm:h-[400px] lg:h-[460px]';

export default function SpaceGalleryBento({
  images = [],
  layout = 'auto',
  statusBadge,
  onVerticalWidth,
}: {
  images?: GalleryImage[];
  // 'hero' forces the single deliberate protagonist regardless of how many photos
  // exist (the design must look great with just 1 cover; extra photos stay reachable
  // through the lightbox). 'auto' keeps the count-adaptive mosaic.
  layout?: 'auto' | 'hero';
  // Badge "Activo" a renderizar DENTRO del hero (pertenece al contenedor del hero, no a las
  // dimensiones de la imagen).
  statusBadge?: React.ReactNode;
  // Publica el ancho de COLUMNA del hero (siempre 400: el hero es cuadrado para toda imagen),
  // para que el bloque de contenido inferior use exactamente ese mismo ancho.
  onVerticalWidth?: (w: number | null) => void;
}) {
  const valid = filterValidImages(images);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [current, setCurrent] = useState(0);

  const next = useCallback(
    () => setCurrent((c) => (c + 1) % valid.length),
    [valid.length]
  );
  const prev = useCallback(
    () => setCurrent((c) => (c - 1 + valid.length) % valid.length),
    [valid.length]
  );

  const isSingle = layout === 'hero' || valid.length === 1;
  // Hero SIEMPRE cuadrado (1:1) + object-cover, para CUALQUIER imagen (vertical, cuadrada u
  // horizontal) y en cualquier tamaño disponible: la portada llena el cuadrado de borde a borde
  // y recorta lo que sobra (vertical → arriba/abajo; horizontal → laterales), priorizando el
  // centro. Depende SOLO de object-cover, NUNCA del dispositivo; sin contain, sin bandas, sin
  // fondos/blur de relleno, sin deformar. w-full + max-w-[400px] = 400px en desktop y fluido en
  // tablet/mobile. El ancho de columna publicado al padre es siempre 400px.
  const heroBoxStyle: React.CSSProperties | undefined = undefined;
  const heroSizeClass = 'w-full max-w-[400px] aspect-square';
  const heroColumnWidth: number | null = 400;
  useEffect(() => {
    onVerticalWidth?.(heroColumnWidth);
  }, [heroColumnWidth, onVerticalWidth]);

  if (valid.length === 0) return null;

  const openAt = (i: number) => () => {
    setCurrent(i);
    setViewerOpen(true);
  };

  // Up to 5 tiles: 1 feature (2x2) + 4 small (1x1). The last visible tile shows a
  // "+N" overlay when more photos exist; it still opens the viewer at that image.
  const tiles = valid.slice(0, 5);
  const remaining = valid.length - tiles.length;

  return (
    <div className="w-full">
      {isSingle ? (
        // Portada única CUADRADA (1:1) con object-cover: cualquier imagen llena el cuadrado de
        // borde a borde, recortando lo que sobra (nunca contain ni bandas). Frame Luma (rounded-16,
        // borde sutil, sombra suave); badge "Activo" dentro del hero. Fuente = profile_image
        // original (nunca thumbnail) optimizada por next/image al tamaño real del hero.
        <button
          type="button"
          onClick={openAt(0)}
          style={heroBoxStyle}
          className={`relative block overflow-hidden rounded-[16px] border border-black/[0.07] shadow-[0_12px_34px_-16px_rgba(15,23,42,0.2)] group/tile cursor-pointer hover:shadow-[0_18px_44px_-16px_rgba(15,23,42,0.26)] transition-all duration-500 ${heroSizeClass}`}
        >
          <Image
            alt={valid[0].alt}
            src={valid[0].url}
            fill
            sizes="(max-width:1024px) 100vw, 440px"
            quality={90}
            className="object-cover object-center transition-transform duration-700 group-hover/tile:scale-105"
            priority
            unoptimized={valid[0].unoptimized}
          />
          <div className="absolute inset-0 bg-black/5 group-hover/tile:bg-transparent transition-colors duration-300" />
          {statusBadge}
        </button>
      ) : (
        <div className={`grid grid-cols-4 grid-rows-2 gap-3 ${HEIGHT}`}>
          {tiles.map((img, i) => {
            const isFeature = i === 0;
            const isLast = i === tiles.length - 1 && remaining > 0;
            return (
              <button
                key={i}
                type="button"
                onClick={openAt(i)}
                className={[
                  'relative overflow-hidden border border-white/20 shadow-lg',
                  'group/tile cursor-pointer hover:shadow-2xl transition-all duration-500',
                  isFeature
                    ? 'col-span-2 row-span-2 rounded-[28px]'
                    : 'col-span-1 row-span-1 rounded-[22px]',
                ].join(' ')}
              >
                <Image
                  alt={img.alt}
                  src={img.url}
                  fill
                  sizes={
                    isFeature
                      ? '(max-width:1024px) 100vw, 460px'
                      : '(max-width:1024px) 50vw, 230px'
                  }
                  className="object-cover transition-transform duration-700 group-hover/tile:scale-110"
                  priority={i === 0}
                  unoptimized={img.unoptimized}
                />
                <div className="absolute inset-0 bg-black/5 group-hover/tile:bg-transparent transition-colors duration-300" />
                {isLast && (
                  <div className="absolute inset-0 bg-black/45 backdrop-blur-[1px] flex items-center justify-center">
                    <span className="text-white text-lg font-semibold tracking-tight">
                      +{remaining}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <ImgsViewer
        imgs={valid.map((i) => ({ src: i.url, alt: i.alt }))}
        currImg={current}
        isOpen={viewerOpen}
        onClickPrev={prev}
        onClickNext={next}
        onClose={() => setViewerOpen(false)}
      />
    </div>
  );
}
